import React, { useState, useEffect } from 'react';
import API from '../services/api'; // Adjust import path as needed
import { useIsMobile } from '../hooks/useIsMobile';
import { useLocation, useNavigate } from 'react-router-dom';

function ImprovedClientProfilePage() {
  const isMobile = useIsMobile();
  // On mobile, show the client list until the user picks a client or "New Client",
  // then show the form with a "Back to clients" button (collapse-on-select).
  const [mobileShowForm, setMobileShowForm] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Client state management
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [isEditingExisting, setIsEditingExisting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [clientIsActive, setClientIsActive] = useState(true);

  // Form data state
  const [formData, setFormData] = useState({
    // Split the client's name into first and last for separate inputs
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    isMinor: false,
    guardianName: '',
    guardianPhone: '',
    diagnosisCode: '',
    dob: '',
    gender: '',
    reasonForVisit: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    billingEmail: '',
    billingAddress: '',
    notes: '',
    medications: '' // Add medications field
  });

  // If navigated here with prefill data (e.g. from the dashboard "new client
  // confirmed" banner), open a blank New Client form populated with it, then
  // clear the navigation state so a refresh doesn't re-prefill.
  useEffect(() => {
    const prefill = location.state?.prefill;
    if (!prefill) return;
    setIsEditingExisting(false);
    setSelectedClientId('');
    setClientIsActive(true);
    setFormData((prev) => ({
      ...prev,
      firstName: prefill.firstName ?? prev.firstName,
      lastName: prefill.lastName ?? prev.lastName,
      email: prefill.email ?? prev.email,
      phone: prefill.phone ?? prev.phone,
      gender: prefill.gender ?? prev.gender,
      dob: prefill.dob ?? prev.dob,
      address: prefill.address ?? prev.address,
      city: prefill.city ?? prev.city,
      state: prefill.state ?? prev.state,
      zipCode: prefill.zipCode ?? prev.zipCode,
      emergencyContactName: prefill.emergencyContactName ?? prev.emergencyContactName,
      emergencyContactPhone: prefill.emergencyContactPhone ?? prev.emergencyContactPhone,
      guardianName: prefill.guardianName ?? prev.guardianName,
      // If Acuity provided a guardian name, surface the guardian fields
      isMinor: prefill.guardianName ? true : prev.isMinor,
      medications: prefill.medications ?? prev.medications,
      reasonForVisit: prefill.reasonForVisit ?? prev.reasonForVisit,
      notes: prefill.notes ?? prev.notes,
    }));
    setMobileShowForm(true);
    navigate(location.pathname, { replace: true });
  }, []);

  // Intake link modal state
  const [showIntakeLinkModal, setShowIntakeLinkModal] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Welcome email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null); // 'success' | 'error' | null
  const [emailClientId, setEmailClientId] = useState('');
  const [emailClientName, setEmailClientName] = useState('');

  // Post-create welcome email prompt
  const [showPostCreateModal, setShowPostCreateModal] = useState(false);
  const [pendingClientId, setPendingClientId] = useState(null);
  const [pendingClientName, setPendingClientName] = useState('');
  const [pendingClientEmail, setPendingClientEmail] = useState('');
  
  // Intake letter modal state
  const [showIntakeLetterModal, setShowIntakeLetterModal] = useState(false);
  const [intakeLetter, setIntakeLetter] = useState('');
  const [letterCopied, setLetterCopied] = useState(false);
  const [savedLetters, setSavedLetters] = useState([]);
  
  // Store the current client's letter in a cache to maintain between modal openings
  const [clientLetterCache, setClientLetterCache] = useState({});

  // Fetch all clients belonging to this therapist
  useEffect(() => {
    fetchClients();
  }, [showInactive]);

  const fetchClients = async () => {
    try {
      const params = showInactive ? '?includeInactive=true' : '';
      const res = await API.get(`/therapist/clients${params}`);
      setClients(res.data); // Should be an array of { id, client_name, is_active }
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  // If user selects an existing client, fetch that client's details
  const handleSelectClient = async (clientId) => {
    setSelectedClientId(clientId);
    if (!clientId) {
      // No client selected - reset form for "new"
      setIsEditingExisting(false);
      setClientIsActive(true);
      resetFormData();
      return;
    }

    try {
      const res = await API.get(`/therapist/clients/${clientId}`);
      if (res.data) {
        // Format the date from database (could be null or a date string)
        let formattedDob = '';
        if (res.data.dob) {
          try {
            // Try to convert the date to YYYY-MM-DD format for the input field
            const dateObj = new Date(res.data.dob);
            if (!isNaN(dateObj)) {
              formattedDob = dateObj.toISOString().split('T')[0];
            }
          } catch (err) {
            console.error("Error formatting date:", err);
            formattedDob = '';
          }
        }

        console.log("Original DOB from DB:", res.data.dob);
        console.log("Formatted DOB for form:", formattedDob);

        setIsEditingExisting(true);
        setClientIsActive(res.data.is_active !== false);
        // Parse the full client name into first and last names.  If the name
        // includes a comma, treat it as "Last, First".  Otherwise, split on
        // whitespace and assign the first token as the first name and the
        // remainder as the last name.
        let firstName = '';
        let lastName = '';
        if (res.data.client_name) {
          const nameStr = res.data.client_name.trim();
          if (nameStr.includes(',')) {
            const parts = nameStr.split(',');
            lastName = parts[0].trim();
            firstName = parts[1] ? parts[1].trim() : '';
          } else {
            const parts = nameStr.split(/\s+/);
            if (parts.length > 1) {
              firstName = parts[0];
              lastName = parts.slice(1).join(' ');
            } else if (parts.length === 1) {
              firstName = parts[0];
              lastName = '';
            }
          }
        }
        setFormData({
          firstName: firstName,
          lastName: lastName,
          phone: res.data.phone || '',
          email: res.data.email || '',
          emergencyContactName: res.data.emergency_contact_name || '',
          emergencyContactPhone: res.data.emergency_contact_phone || '',
          isMinor: !!res.data.is_minor,
          guardianName: res.data.guardian_name || '',
          guardianPhone: res.data.guardian_phone || '',
          diagnosisCode: res.data.diagnosis_code || '',
          dob: formattedDob,
          gender: res.data.gender || '',
          reasonForVisit: res.data.reason_for_visit || '',
          address: res.data.address || '',
          city: res.data.city || '',
          state: res.data.state || '',
          zipCode: res.data.zip_code || '',
          billingEmail: res.data.billing_email || '',
          billingAddress: res.data.billing_address || '',
          notes: res.data.notes || '',
          medications: res.data.medications || ''
        });
      }
    } catch (err) {
      console.error('Error fetching client details:', err);
    }
  };

  // Reset form data to default values
  const resetFormData = () => {
    setFormData({
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      emergencyContactName: '',
      emergencyContactPhone: '',
      isMinor: false,
      guardianName: '',
      guardianPhone: '',
      diagnosisCode: '',
      dob: '',
      gender: '',
      reasonForVisit: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      billingEmail: '',
      billingAddress: '',
      notes: '',
      medications: ''
    });
  };

  // Handle date of birth changes
  const handleDobChange = (e) => {
    const dateValue = e.target.value;
    console.log("Date input value:", dateValue);
    
    // Always update the form state with the input value
    setFormData({
      ...formData,
      dob: dateValue
    });
  };

  // Create new client
  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      // Compute the combined client name from first and last names.  When both
      // names are present, format as "Last, First" to leverage the server's
      // capitalization logic.  Otherwise, just use whichever name is provided.
      let combinedClientName = '';
      if (formData.firstName && formData.lastName) {
        combinedClientName = `${formData.lastName}, ${formData.firstName}`;
      } else if (formData.firstName) {
        combinedClientName = formData.firstName;
      } else if (formData.lastName) {
        combinedClientName = formData.lastName;
      }
      const response = await API.post('/therapist/clients', {
        clientName: combinedClientName,
        phone: formData.phone,
        email: formData.email,
        emergencyContactName: formData.emergencyContactName,
        emergencyContactPhone: formData.emergencyContactPhone,
        isMinor: formData.isMinor,
        guardianName: formData.guardianName,
        guardianPhone: formData.guardianPhone,
        diagnosisCode: formData.diagnosisCode,
        dob: formData.dob,
        gender: formData.gender,
        reasonForVisit: formData.reasonForVisit,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        billingEmail: formData.billingEmail,
        billingAddress: formData.billingAddress,
        notes: formData.notes,
        medications: formData.medications
      });
      
      const newClientId = response.data.clientId;

      // Store for post-create welcome email prompt
      setPendingClientId(newClientId);
      setPendingClientName(combinedClientName);
      setPendingClientEmail(formData.email || formData.billingEmail || '');
      setShowPostCreateModal(true);

      // Reset and refresh
      resetFormData();
      setSelectedClientId('');
      setIsEditingExisting(false);
      setMobileShowForm(false);
      fetchClients();
    } catch (err) {
      console.error('Error creating client:', err);
      alert('Error creating client');
    }
  };

  // Update existing client
  const handleUpdateClient = async (e) => {
    e.preventDefault();
    if (!selectedClientId) {
      alert('No client selected for update');
      return;
    }
    try {
      // Compute a combined client name from first and last names.  When both
      // names are provided, format as "Last, First"; otherwise, use whatever
      // name information is available.  This matches the server's
      // capitalization logic.
      let combinedClientName = '';
      if (formData.firstName && formData.lastName) {
        combinedClientName = `${formData.lastName}, ${formData.firstName}`;
      } else if (formData.firstName) {
        combinedClientName = formData.firstName;
      } else if (formData.lastName) {
        combinedClientName = formData.lastName;
      }
      await API.put(`/therapist/clients/${selectedClientId}`, {
        clientName: combinedClientName,
        phone: formData.phone,
        email: formData.email,
        emergencyContactName: formData.emergencyContactName,
        emergencyContactPhone: formData.emergencyContactPhone,
        isMinor: formData.isMinor,
        guardianName: formData.guardianName,
        guardianPhone: formData.guardianPhone,
        diagnosisCode: formData.diagnosisCode,
        dob: formData.dob,
        gender: formData.gender,
        reasonForVisit: formData.reasonForVisit,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        billingEmail: formData.billingEmail,
        billingAddress: formData.billingAddress,
        notes: formData.notes,
        medications: formData.medications
      });
      alert('Client updated successfully');
      fetchClients();
    } catch (err) {
      console.error('Error updating client:', err);
      alert('Error updating client');
    }
  };

  // Handle intake link generation
  const handleGenerateIntakeLink = async (clientId = null) => {
    const targetId = clientId || selectedClientId;
    
    if (!targetId) {
      alert('Please select a client first');
      return;
    }
    
    setShowIntakeLinkModal(true);
    setGeneratedLink('');
    setErrorMsg('');
    setLoading(true);
    
    try {
      // Calculate date 7 days from now for auto-expiration
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + 7);
      
      const body = {
        clientId: targetId,
        expiresAt: expirationDate.toISOString()
      };

      const res = await API.post('/forms/intake/generate', body);

      if (res.data.success) {
        setGeneratedLink(res.data.link);
        
        // Use client letter from cache if available, otherwise create default
        if (clientLetterCache[targetId]) {
          setIntakeLetter(clientLetterCache[targetId]);
        } else {
          // Derive a friendly full name for the greeting.  When both names
          // are present, join them with a space; otherwise, use whichever
          // name is available.  This ensures the letter always has a name
          // placeholder.
          const fullName = [formData.firstName, formData.lastName]
            .filter(Boolean)
            .join(' ')
            .trim();
          // Create default intake letter with the client's name
          const defaultIntakeLetter = `Hello ${fullName},

Welcome to Avenues Unlimited Counseling Center! To help us get started, please complete our intake form by clicking the link below:

${res.data.link}

This link will expire in 7 days. The form should take about 10-15 minutes to complete. Your information will be securely stored and will help us provide you with the best care possible.

If you have any questions or need assistance, please don't hesitate to contact us at 502-459-9635.

We look forward to working with you!

Avenues Unlimited Counseling Center`;
          setIntakeLetter(defaultIntakeLetter);
          
          // Save to cache for this client
          setClientLetterCache(prev => ({
            ...prev,
            [targetId]: defaultIntakeLetter
          }));
        }
      } else {
        setErrorMsg(res.data.error || 'Error generating link.');
      }
    } catch (err) {
      console.error('Error generating link:', err);
      setErrorMsg('Server error generating link.');
    } finally {
      setLoading(false);
    }
  };

  // Copy link to clipboard
  const copyLinkToClipboard = () => {
    if (!generatedLink) return;
    
    navigator.clipboard.writeText(generatedLink)
      .then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 3000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  // Copy intake letter to clipboard
  const copyIntakeLetterToClipboard = () => {
    if (!intakeLetter) return;
    
    navigator.clipboard.writeText(intakeLetter)
      .then(() => {
        setLetterCopied(true);
        setTimeout(() => setLetterCopied(false), 3000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };
  
  // Save the current intake letter
  const saveIntakeLetter = () => {
    if (!intakeLetter) return;
    
    // Save to client letter cache
    setClientLetterCache(prev => ({
      ...prev,
      [selectedClientId]: intakeLetter
    }));
    
    const fullName = [formData.firstName, formData.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    const newSavedLetter = {
      id: Date.now(),
      clientName: fullName,
      clientId: selectedClientId,
      content: intakeLetter,
      date: new Date().toISOString()
    };
    
    // Save locally and can also save to API if needed
    setSavedLetters(prev => [...prev, newSavedLetter]);
    
    // Optionally save to backend:
    // API.post('/therapist/client-letters', newSavedLetter)
    //   .then(res => console.log('Letter saved'))
    //   .catch(err => console.error('Error saving letter:', err));
    
    alert('Intake letter saved successfully');
  };

  // Update letter when user types
  const handleLetterChange = (e) => {
    const newContent = e.target.value;
    setIntakeLetter(newContent);
    
    // Update cache in real-time
    if (selectedClientId) {
      setClientLetterCache(prevCache => ({
        ...prevCache,
        [selectedClientId]: newContent
      }));
    }
  };

  // Open welcome email modal, pre-filling from profile default message
  const handleOpenEmailModal = async (overrideClientId = null, overrideEmail = null, overrideName = null) => {
    const useClientId = overrideClientId || selectedClientId;
    if (!useClientId) { alert('Please select a client first'); return; }
    setEmailClientId(useClientId);
    setEmailClientName(overrideName || '');
    setEmailTo(overrideEmail || formData.email || formData.billingEmail || '');
    setEmailStatus(null);
    try {
      const res = await API.get('/therapist/profile');
      setEmailMessage(res.data.default_welcome_message || '');
    } catch (_) {
      setEmailMessage('');
    }
    setShowEmailModal(true);
  };

  const handleSendWelcomeEmail = async () => {
    if (!emailTo) { alert('Please enter a recipient email'); return; }
    if (!emailMessage.trim()) { alert('Please enter a message'); return; }
    setEmailSending(true);
    setEmailStatus(null);
    try {
      await API.post('/therapist/send-welcome-email', {
        clientId: emailClientId || selectedClientId,
        recipientEmail: emailTo,
        message: emailMessage,
      });
      setEmailStatus('success');
    } catch (err) {
      console.error('Email send error:', err);
      setEmailStatus('error');
    } finally {
      setEmailSending(false);
    }
  };

  // Toggle client active/inactive status
  const toggleClientStatus = async () => {
    if (!selectedClientId) return;
    const newStatus = !clientIsActive;
    try {
      await API.patch("/therapist/clients/" + selectedClientId + "/status", {
        isActive: newStatus
      });
      setClientIsActive(newStatus);
      alert("Client marked as " + (newStatus ? "active" : "inactive"));
      fetchClients();
    } catch (err) {
      console.error("Error toggling client status:", err);
      alert("Error updating client status");
    }
  };

  // Filter clients based on search term
  const filteredClients = clients.filter(client =>
    client.client_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Responsive overrides (inline styles can't use CSS media queries)
  const containerStyle = isMobile ? { ...styles.container, padding: '12px' } : styles.container;
  const headerStyle = isMobile
    ? { ...styles.header, flexDirection: 'column', alignItems: 'stretch', gap: '12px' }
    : styles.header;
  const clientListContainerStyle = isMobile
    ? { ...styles.clientListContainer, width: '100%', minWidth: 0, flex: '1 1 100%' }
    : styles.clientListContainer;
  const clientListStyle = isMobile ? { ...styles.clientList, maxHeight: 'none' } : styles.clientList;
  const formContainerStyle = isMobile ? { ...styles.formContainer, width: '100%', flex: '1 1 100%' } : styles.formContainer;
  const formHeaderStyle = isMobile
    ? { ...styles.formHeader, flexDirection: 'column', alignItems: 'flex-start', gap: '10px' }
    : styles.formHeader;
  const formRowStyle = isMobile ? { ...styles.formRow, flexDirection: 'column', gap: '12px' } : styles.formRow;
  const showList = !isMobile || !mobileShowForm;
  const showForm = !isMobile || mobileShowForm;

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h2 style={styles.title}>Client Profiles</h2>
        <div style={styles.searchBar}>
          <input
            type="text"
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>
      </div>

      <div style={styles.contentWrapper}>
        {/* LEFT PANEL - Client List */}
        {showList && (
        <div style={clientListContainerStyle}>
          <div style={styles.listHeader}>
            <h3 style={styles.listTitle}>Client List</h3>
            <button
              onClick={() => { handleSelectClient(''); setMobileShowForm(true); }}
              style={styles.newClientButton}
            >
              + New Client
            </button>
          </div>
          <div style={{display: 'flex', alignItems: 'center', padding: '8px 16px', borderBottom: '1px solid var(--border-color)', backgroundColor: 'var(--surface-muted)'}}>
            <label style={{display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '13px', color: 'var(--text-muted)'}}>
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                style={{marginRight: '6px'}}
              />
              Show Inactive Clients
            </label>
          </div>
          
          <div style={clientListStyle}>
            {filteredClients.length > 0 ? (
              filteredClients.map((client) => (
                <div
                  key={client.id}
                  style={{
                    ...styles.clientItem,
                    backgroundColor: selectedClientId === client.id ? '#e6f7ff' : (client.is_active === false ? 'var(--surface-muted)' : '#fff'),
                    borderLeft: selectedClientId === client.id ? '4px solid #1890ff' : 'none',
                    paddingLeft: selectedClientId === client.id ? '12px' : '16px',
                    opacity: client.is_active === false ? 0.6 : 1
                  }}
                  onClick={() => { handleSelectClient(client.id); setMobileShowForm(true); }}
                >
                  <div style={styles.clientName}>
                    {client.client_name}
                    {client.is_active === false && (
                      <span style={{marginLeft: '8px', fontSize: '11px', color: '#ff4d4f', fontWeight: 'bold'}}>(Inactive)</span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div style={styles.noClients}>
                {searchTerm ? 'No clients match your search' : 'No clients found'}
              </div>
            )}
          </div>
        </div>
        )}

        {/* RIGHT PANEL - Client Form */}
        {showForm && (
        <div style={formContainerStyle}>
          {isMobile && (
            <button
              type="button"
              style={styles.backToClientsButton}
              onClick={() => setMobileShowForm(false)}
            >
              ← Back to clients
            </button>
          )}
          <form
            onSubmit={isEditingExisting ? handleUpdateClient : handleCreateClient}
            style={styles.form}
          >
            <div style={formHeaderStyle}>
              <h3 style={styles.formTitle}>
                {isEditingExisting ? 'Edit Client' : 'New Client'}
              </h3>

              {isEditingExisting && (
                <div style={{display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap'}}>
                  <button
                    type="button"
                    onClick={toggleClientStatus}
                    style={{
                      backgroundColor: clientIsActive ? '#ff4d4f' : '#52c41a',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    {clientIsActive ? 'Mark Inactive' : 'Mark Active'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleGenerateIntakeLink()}
                    style={styles.generateLinkButton}
                  >
                    Generate Intake Link
                  </button>
                  <button
                    type="button"
                    onClick={() => handleOpenEmailModal(
                      selectedClientId,
                      formData.email || formData.billingEmail || '',
                      [formData.firstName, formData.lastName].filter(Boolean).join(' ').trim()
                    )}
                    style={{ ...styles.generateLinkButton, backgroundColor: '#1890ff', marginLeft: '8px' }}
                  >
                    Send Welcome Email
                  </button>
                </div>
              )}
            </div>
            
            <div style={styles.formContent}>
              {/* Inactive client banner */}
              {isEditingExisting && !clientIsActive && (
                <div style={{
                  backgroundColor: '#fff2e8',
                  border: '1px solid #ffbb96',
                  borderRadius: '4px',
                  padding: '10px 16px',
                  marginBottom: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{color: '#d4380d', fontSize: '14px'}}>
                    This client is currently inactive and will not appear in session logs or notes pages.
                  </span>
                </div>
              )}
              {/* Form Sections */}
              <div style={styles.formSection}>
                <h4 style={styles.sectionTitle}>Basic Information</h4>
                {/* Split the client's name into separate first and last name fields */}
                <div style={formRowStyle}>
                  <div style={styles.formField}>
                    <label style={styles.label}>First Name*</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      style={styles.input}
                      required
                    />
                  </div>
                  <div style={styles.formField}>
                    <label style={styles.label}>Last Name*</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      style={styles.input}
                      required
                    />
                  </div>
                </div>
                <div style={formRowStyle}>
                  <div style={styles.formField}>
                    <label style={styles.label}>Date of Birth</label>
                    <input
                      type="date"
                      value={formData.dob}
                      onChange={handleDobChange}
                      style={styles.input}
                    />
                  </div>
                </div>

                <div style={formRowStyle}>
                  <div style={styles.formField}>
                    <label style={styles.label}>Gender</label>
                    <select
                      value={formData.gender}
                      onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                      style={styles.input}
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Non-binary">Non-binary</option>
                      <option value="Other">Other</option>
                      <option value="Prefer not to say">Prefer not to say</option>
                    </select>
                  </div>
                  <div style={styles.formField}>
                    <label style={styles.label}>Diagnosis Code</label>
                    <input
                      type="text"
                      value={formData.diagnosisCode}
                      onChange={(e) => setFormData({ ...formData, diagnosisCode: e.target.value })}
                      style={styles.input}
                    />
                  </div>
                </div>

                <div style={formRowStyle}>
                  <div style={styles.formField} style={{width: '100%'}}>
                    <label style={styles.label}>Reason for Visit</label>
                    <textarea
                      value={formData.reasonForVisit}
                      onChange={(e) => setFormData({ ...formData, reasonForVisit: e.target.value })}
                      style={styles.textarea}
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              <div style={styles.formSection}>
                <h4 style={styles.sectionTitle}>Contact Information</h4>
                <div style={formRowStyle}>
                  <div style={styles.formField}>
                    <label style={styles.label}>Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.formField}>
                    <label style={styles.label}>Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      style={styles.input}
                    />
                  </div>
                </div>

                <div style={formRowStyle}>
                  <div style={styles.formField} style={{width: '100%'}}>
                    <label style={styles.label}>Address</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      style={styles.input}
                      placeholder="Street Address"
                    />
                  </div>
                </div>

                <div style={formRowStyle}>
                  <div style={styles.formField}>
                    <label style={styles.label}>City</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.formField}>
                    <label style={styles.label}>State</label>
                    <input
                      type="text"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.formField}>
                    <label style={styles.label}>Zip Code</label>
                    <input
                      type="text"
                      value={formData.zipCode}
                      onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                      style={styles.input}
                    />
                  </div>
                </div>
              </div>

              <div style={styles.formSection}>
                <h4 style={styles.sectionTitle}>
                  <span style={styles.checkboxWrapper}>
                    <input
                      type="checkbox"
                      checked={formData.isMinor}
                      onChange={(e) => setFormData({ ...formData, isMinor: e.target.checked })}
                      id="isMinorCheckbox"
                    />
                    <label htmlFor="isMinorCheckbox" style={styles.checkboxLabel}>Client is a Minor</label>
                  </span>
                </h4>
                
                {formData.isMinor && (
                  <div style={styles.minorSection}>
                    <div style={formRowStyle}>
                      <div style={styles.formField}>
                        <label style={styles.label}>Guardian Name</label>
                        <input
                          type="text"
                          value={formData.guardianName}
                          onChange={(e) => setFormData({ ...formData, guardianName: e.target.value })}
                          style={styles.input}
                        />
                      </div>
                      <div style={styles.formField}>
                        <label style={styles.label}>Guardian Phone</label>
                        <input
                          type="tel"
                          value={formData.guardianPhone}
                          onChange={(e) => setFormData({ ...formData, guardianPhone: e.target.value })}
                          style={styles.input}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div style={styles.formSection}>
                <h4 style={styles.sectionTitle}>Emergency Contact</h4>
                <div style={formRowStyle}>
                  <div style={styles.formField}>
                    <label style={styles.label}>Name</label>
                    <input
                      type="text"
                      value={formData.emergencyContactName}
                      onChange={(e) => setFormData({ ...formData, emergencyContactName: e.target.value })}
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.formField}>
                    <label style={styles.label}>Phone</label>
                    <input
                      type="tel"
                      value={formData.emergencyContactPhone}
                      onChange={(e) => setFormData({ ...formData, emergencyContactPhone: e.target.value })}
                      style={styles.input}
                    />
                  </div>
                </div>
              </div>

              {/* Add new Medications section */}
              <div style={styles.formSection}>
                <h4 style={styles.sectionTitle}>Current Medications</h4>
                <div style={formRowStyle}>
                  <div style={styles.formField} style={{width: '100%'}}>
                    <textarea
                      value={formData.medications}
                      onChange={(e) => setFormData({ ...formData, medications: e.target.value })}
                      style={styles.textarea}
                      rows={3}
                      placeholder="List all current medications, dosages, and frequencies..."
                    />
                  </div>
                </div>
              </div>

              <div style={styles.formSection}>
                <h4 style={styles.sectionTitle}>Billing Information</h4>
                <div style={formRowStyle}>
                  <div style={styles.formField}>
                    <label style={styles.label}>Billing Email</label>
                    <input
                      type="email"
                      value={formData.billingEmail}
                      onChange={(e) => setFormData({ ...formData, billingEmail: e.target.value })}
                      style={styles.input}
                    />
                  </div>
                </div>
                <div style={formRowStyle}>
                  <div style={styles.formField} style={{width: '100%'}}>
                    <label style={styles.label}>Billing Address</label>
                    <textarea
                      value={formData.billingAddress}
                      onChange={(e) => setFormData({ ...formData, billingAddress: e.target.value })}
                      style={styles.textarea}
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              <div style={styles.formSection}>
                <h4 style={styles.sectionTitle}>Additional Notes</h4>
                <div style={formRowStyle}>
                  <div style={styles.formField} style={{width: '100%'}}>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      style={styles.textarea}
                      rows={3}
                      placeholder="Add any additional notes about this client here..."
                    />
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.formActions}>
              <button type="submit" style={styles.submitButton}>
                {isEditingExisting ? 'Update Client' : 'Create Client'}
              </button>
              {isEditingExisting && (
                <button
                  type="button"
                  onClick={() => { handleSelectClient(''); setMobileShowForm(false); }}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
        )}
      </div>

      {/* Intake Link Generation Modal */}
      {showIntakeLinkModal && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Generate Intake Form Link</h3>
              <button 
                style={styles.modalClose}
                onClick={() => setShowIntakeLinkModal(false)}
              >
                ×
              </button>
            </div>
            
            <div style={styles.modalBody}>
              {loading ? (
                <div style={styles.loadingContainer}>
                  <p>Generating link...</p>
                </div>
              ) : errorMsg ? (
                <div style={styles.errorContainer}>
                  <p style={styles.errorMessage}>{errorMsg}</p>
                  <button 
                    style={styles.modalButton}
                    onClick={() => setShowIntakeLinkModal(false)}
                  >
                    Close
                  </button>
                </div>
              ) : generatedLink ? (
                <div>
                  <p>The intake form link has been generated for <strong>{[formData.firstName, formData.lastName].filter(Boolean).join(' ')}</strong>:</p>
                  
                  <div style={styles.linkContainer}>
                    <a href={generatedLink} target="_blank" rel="noreferrer">{generatedLink}</a>
                  </div>
                  
                  <p style={styles.linkExpires}>
                    This link will expire in 7 days on {new Date(new Date().setDate(new Date().getDate() + 7)).toLocaleDateString()}.
                  </p>
                  
                  <div style={styles.modalActions}>
                    <button 
                      style={linkCopied ? styles.buttonSuccess : styles.modalButton}
                      onClick={copyLinkToClipboard}
                    >
                      {linkCopied ? 'Copied!' : 'Copy Link'}
                    </button>
                    
                    <button 
                      style={styles.modalButtonPrimary}
                      onClick={() => {
                        setShowIntakeLinkModal(false);
                        setShowIntakeLetterModal(true);
                      }}
                    >
                      Prepare Intake Letter
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <p>
                    Generate an intake form link for <strong>{[formData.firstName, formData.lastName].filter(Boolean).join(' ')}</strong> to complete.
                    The link will automatically expire in 7 days.
                  </p>
                  
                  <div style={styles.modalActions}>
                    <button 
                      style={styles.modalButtonPrimary}
                      onClick={() => handleGenerateIntakeLink()}
                    >
                      Generate Link
                    </button>
                    <button 
                      style={styles.modalButton}
                      onClick={() => setShowIntakeLinkModal(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Intake Letter Modal */}
      {showIntakeLetterModal && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modal} style={{width: '600px'}}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Client Intake Letter</h3>
              <button 
                style={styles.modalClose}
                onClick={() => setShowIntakeLetterModal(false)}
              >
                ×
              </button>
            </div>
            
            <div style={styles.modalBody}>
              <p>Customize the intake letter for {[formData.firstName, formData.lastName].filter(Boolean).join(' ')}:</p>
              
              <textarea
                value={intakeLetter}
                onChange={handleLetterChange}
                style={{
                  ...styles.intakeLetterTextarea,
                  height: '320px',
                  overflowY: 'auto',
                  resize: 'vertical'
                }}
              />
              
              <div style={styles.modalActions}>
                <button 
                  style={letterCopied ? styles.buttonSuccess : styles.modalButtonPrimary}
                  onClick={copyIntakeLetterToClipboard}
                >
                  {letterCopied ? 'Copied!' : 'Copy Letter'}
                </button>
                
                <button 
                  style={styles.saveButton}
                  onClick={saveIntakeLetter}
                >
                  Save Letter
                </button>
                
                <button 
                  style={styles.modalButton}
                  onClick={() => setShowIntakeLetterModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Welcome Email Modal */}
      {/* Post-create welcome email prompt */}
      {showPostCreateModal && (
        <div style={styles.modalBackdrop}>
          <div style={{ ...styles.modal, width: '440px' }}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Client Created</h3>
              <button style={styles.modalClose} onClick={() => setShowPostCreateModal(false)}>×</button>
            </div>
            <div style={styles.modalBody}>
              <p style={{ marginTop: 0 }}>
                <strong>{pendingClientName}</strong> has been added successfully.
              </p>
              <p>Would you like to send them a welcome email with their intake form link?</p>
              <div style={styles.modalActions}>
                <button
                  style={styles.modalButtonPrimary}
                  onClick={() => {
                    setShowPostCreateModal(false);
                    handleOpenEmailModal(pendingClientId, pendingClientEmail, pendingClientName);
                  }}
                >
                  Yes, Send Welcome Email
                </button>
                <button style={styles.modalButton} onClick={() => setShowPostCreateModal(false)}>
                  Skip
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Welcome email compose + preview modal */}
      {showEmailModal && (
        <div style={styles.modalBackdrop}>
          <div style={{ ...styles.modal, width: '900px', maxWidth: '95vw' }}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Send Welcome Email</h3>
              <button style={styles.modalClose} onClick={() => setShowEmailModal(false)}>×</button>
            </div>
            <div style={styles.modalBody}>
              {emailStatus === 'success' ? (
                <div>
                  <p style={{ color: '#065F46', fontWeight: 'bold' }}>Email sent successfully!</p>
                  <p>A welcome email with the intake form link has been sent to <strong>{emailTo}</strong>.</p>
                  <div style={styles.modalActions}>
                    <button style={styles.modalButtonPrimary} onClick={() => setShowEmailModal(false)}>Done</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                  {/* Left: compose fields */}
                  <div style={{ flex: '0 0 300px', minWidth: '260px', width: '100%' }}>
                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>To</label>
                      <input
                        type="email"
                        value={emailTo}
                        onChange={e => setEmailTo(e.target.value)}
                        style={{ width: '100%', padding: '8px', border: '1px solid var(--border-color)', borderRadius: '4px', boxSizing: 'border-box' }}
                        placeholder="client@example.com"
                      />
                    </div>
                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Message</label>
                      <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 6px' }}>
                        Edit your message — the intake form link is added automatically.
                      </p>
                      <textarea
                        value={emailMessage}
                        onChange={e => setEmailMessage(e.target.value)}
                        rows={12}
                        style={{ width: '100%', padding: '8px', border: '1px solid var(--border-color)', borderRadius: '4px', fontFamily: 'inherit', fontSize: '13px', resize: 'vertical', boxSizing: 'border-box' }}
                        placeholder="Enter your welcome message here..."
                      />
                    </div>
                    {emailStatus === 'error' && (
                      <p style={{ color: '#f5222d', marginBottom: '10px' }}>Failed to send email. Please try again.</p>
                    )}
                    <div style={styles.modalActions}>
                      <button
                        style={{ ...styles.modalButtonPrimary, opacity: emailSending ? 0.7 : 1 }}
                        onClick={handleSendWelcomeEmail}
                        disabled={emailSending}
                      >
                        {emailSending ? 'Sending...' : 'Send Email'}
                      </button>
                      <button style={styles.modalButton} onClick={() => setShowEmailModal(false)}>Cancel</button>
                    </div>
                  </div>

                  {/* Right: live email preview */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', fontSize: '13px', color: 'var(--text-strong)' }}>
                      Preview — what the client will see
                    </label>
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: '6px', overflow: 'hidden', fontSize: '13px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', maxHeight: '420px', overflowY: 'auto' }}>
                      <div style={{ background: 'var(--primary-color)', padding: '18px', textAlign: 'center' }}>
                        <div style={{ color: '#fff', fontSize: '15px', fontWeight: 'bold' }}>Avenues Unlimited Counseling Center</div>
                      </div>
                      <div style={{ padding: '20px', background: '#fff' }}>
                        {emailMessage
                          ? emailMessage.split('\n').map((line, i) => (
                              <p key={i} style={{ margin: '0 0 8px', lineHeight: 1.5 }}>{line || <br />}</p>
                            ))
                          : <p style={{ color: '#aaa', fontStyle: 'italic' }}>Your message will appear here...</p>
                        }
                        <div style={{ textAlign: 'center', margin: '24px 0' }}>
                          <span style={{ background: 'var(--primary-color)', color: '#fff', padding: '11px 22px', borderRadius: '6px', fontSize: '14px', fontWeight: 'bold', display: 'inline-block' }}>
                            Complete Your Intake Form →
                          </span>
                        </div>
                        <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>
                          A unique intake form link will be included. Link expires in 7 days.
                        </p>
                      </div>
                      <div style={{ background: 'var(--surface-muted)', padding: '12px', textAlign: 'center', fontSize: '11px', color: '#999' }}>
                        Avenues Unlimited Counseling Center &bull; 1774 Mellwood Ave, Louisville, KY 40206 &bull; 502.459.9635
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: 'Arial, sans-serif'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  title: {
    fontSize: '24px',
    margin: 0,
    color: 'var(--text-strong)'
  },
  searchBar: {
    width: '100%',
    maxWidth: '360px'
  },
  searchInput: {
    width: '100%',
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid var(--border-color)',
    borderRadius: '4px'
  },
  contentWrapper: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap',
    alignItems: 'flex-start'
  },

  // Client list styles
  clientListContainer: {
    width: '300px',
    minWidth: '260px',
    flex: '0 0 300px',
    border: '1px solid var(--border-color)',
    borderRadius: '6px',
    backgroundColor: 'var(--surface-muted)',
    overflow: 'hidden'
  },
  listHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid var(--border-color)',
    backgroundColor: '#f1f1f1'
  },
  listTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 'normal'
  },
  newClientButton: {
    backgroundColor: '#1890ff',
    color: 'white',
    border: 'none',
    padding: '6px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px'
  },
  clientList: {
    maxHeight: '650px',
    overflowY: 'auto'
  },
  clientItem: {
    padding: '12px 16px',
    borderBottom: '1px solid var(--border-color)',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  clientName: {
    fontSize: '14px'
  },
  noClients: {
    padding: '15px',
    textAlign: 'center',
    color: '#999',
    fontStyle: 'italic'
  },
  
  // Form container styles
  formContainer: {
    flex: 1,
    border: '1px solid var(--border-color)',
    borderRadius: '6px',
    backgroundColor: '#fff'
  },
  backToClientsButton: {
    display: 'block',
    width: '100%',
    padding: '12px',
    fontSize: '15px',
    fontWeight: '600',
    color: 'var(--primary-color)',
    backgroundColor: '#e6f7ff',
    border: 'none',
    borderBottom: '1px solid #bae0ff',
    borderRadius: '6px 6px 0 0',
    cursor: 'pointer',
    textAlign: 'left'
  },
  form: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  formHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '15px 20px',
    borderBottom: '1px solid var(--border-color)',
    backgroundColor: 'var(--surface-muted)'
  },
  formTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 'normal'
  },
  generateLinkButton: {
    backgroundColor: 'var(--primary-color)',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  formContent: {
    padding: '20px',
    overflowY: 'auto',
    flex: 1
  },
  formSection: {
    marginBottom: '25px'
  },
  sectionTitle: {
    fontSize: '16px',
    marginTop: 0,
    marginBottom: '15px',
    paddingBottom: '6px',
    borderBottom: '1px solid var(--border-color)'
  },
  checkboxWrapper: {
    display: 'flex',
    alignItems: 'center'
  },
  checkboxLabel: {
    marginLeft: '8px',
    cursor: 'pointer'
  },
  minorSection: {
    padding: '10px',
    background: 'var(--surface-muted)',
    borderRadius: '4px',
    marginBottom: '15px'
  },
  formRow: {
    display: 'flex',
    gap: '15px',
    marginBottom: '15px'
  },
  formField: {
    flex: 1,
    width: '100%'
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    color: 'var(--text-strong)'
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid var(--border-color)',
    borderRadius: '4px'
  },
  textarea: {
    width: '100%',
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid var(--border-color)',
    borderRadius: '4px',
    resize: 'vertical'
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '15px 20px',
    borderTop: '1px solid var(--border-color)',
    backgroundColor: 'var(--surface-muted)',
    gap: '10px'
  },
  submitButton: {
    backgroundColor: 'var(--primary-color)',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  cancelButton: {
    backgroundColor: '#fff',
    color: 'var(--text-strong)',
    border: '1px solid var(--border-color)',
    padding: '10px 20px',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  
  // Modal styles
  modalBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  modal: {
    width: '500px',
    backgroundColor: '#ffffff',
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    overflow: 'hidden',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '15px 20px',
    backgroundColor: 'var(--primary-color)',
    flexShrink: 0
  },
  modalTitle: {
    margin: 0,
    fontSize: '18px',
    color: '#ffffff'
  },
  modalClose: {
    background: 'none',
    border: 'none',
    fontSize: '22px',
    cursor: 'pointer',
    color: '#ffffff',
    lineHeight: 1
  },
  modalBody: {
    padding: '20px',
    backgroundColor: '#ffffff',
    overflowY: 'auto',
    flex: 1
  },
  linkContainer: {
    marginTop: '15px',
    padding: '10px',
    background: 'var(--surface-muted)',
    borderRadius: '4px',
    wordBreak: 'break-all'
  },
  linkExpires: {
    color: '#777',
    fontSize: '14px',
    marginTop: '10px'
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '20px'
  },
  modalButton: {
    backgroundColor: 'var(--surface-muted)',
    color: 'var(--text-strong)',
    border: '1px solid var(--border-color)',
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  modalButtonPrimary: {
    backgroundColor: 'var(--primary-color)',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  buttonSuccess: {
    backgroundColor: 'var(--accent-color)',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '20px 0'
  },
  errorContainer: {
    textAlign: 'center'
  },
  errorMessage: {
    color: '#f5222d',
    marginBottom: '15px'
  },
  intakeLetterTextarea: {
    width: '100%',
    padding: '10px',
    border: '1px solid var(--border-color)',
    borderRadius: '4px',
    fontSize: '14px',
    lineHeight: '1.5',
    resize: 'vertical',
    fontFamily: 'monospace',
    backgroundColor: '#ffffff', /* Explicitly white */
    color: '#000000',  /* Explicitly black text */
    outline: 'none',
    position: 'static',
    overflow: 'auto'
  },
  saveButton: {
    backgroundColor: 'var(--accent-color)',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer'
  }
};

export default ImprovedClientProfilePage;